import { css } from "styled-system/css";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FadeIn } from "@/components/ui/animated";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/providers/AuthProvider";
import { displayApiError, EXTERNAL_ISSUES_MESSAGE } from "@/lib/client-safe-error";
import { Logo } from "@/components/brand/logo";

const RECAPTCHA_SITE_KEY = "6LfblHItAAAAAGltsC2ShNDRmEXopTUytiR5SfxR";

declare global {
  interface Window {
    grecaptcha?: {
      enterprise: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

async function getRecaptchaToken(action: string): Promise<string | null> {
  return new Promise((resolve) => {
    if (!window.grecaptcha?.enterprise) {
      resolve(null);
      return;
    }
    window.grecaptcha.enterprise.ready(async () => {
      try {
        const token = await window.grecaptcha!.enterprise.execute(RECAPTCHA_SITE_KEY, { action });
        resolve(token);
      } catch {
        resolve(null);
      }
    });
  });
}

export function LoginPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const showInvalid = sp.get("error") === "invalid_credentials";
  const showResetOk = sp.get("reset") === "1";
  const callbackUrl = sp.get("callbackUrl");

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "SUPERADMIN" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    setBusy(true);
    const recaptchaToken = await getRecaptchaToken("LOGIN");
    const r = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, recaptchaToken }),
      credentials: "include",
    });
    const data = (await r.json().catch(() => ({}))) as { user?: { role: string }; error?: string };
    setBusy(false);
    if (!r.ok) {
      if (r.status === 401) {
        setError("Email o contraseña incorrectos.");
      } else {
        setError(displayApiError(data.error, EXTERNAL_ISSUES_MESSAGE));
      }
      return;
    }
    const logged = data.user;
    if (!logged) {
      setError("Respuesta inválida del servidor.");
      return;
    }
    await refresh();
    const next =
      callbackUrl && callbackUrl.startsWith("/") && !callbackUrl.startsWith("//")
        ? callbackUrl
        : logged.role === "SUPERADMIN"
          ? "/admin"
          : "/dashboard";
    navigate(next, { replace: true });
  }

  return (
    <main
      className={css({
        minH: "100vh",
        display: "grid",
        gridTemplateColumns: { base: "1fr", md: "1fr 1fr" },
        bg: "#f4f6f8",
      })}
    >
      <section
        className={css({
          display: { base: "none", md: "flex" },
          flexDir: "column",
          justifyContent: "space-between",
          p: "10",
          color: "white",
          bg: "#0b1220",
          position: "relative",
          overflow: "hidden",
          _before: {
            content: "\"\"",
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(rgba(99, 102, 241, 0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(99, 102, 241, 0.08) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            opacity: 0.8,
            pointerEvents: "none",
          },
          _after: {
            content: "\"\"",
            position: "absolute",
            inset: 0,
            background: "radial-gradient(700px 380px at 15% 8%, rgba(99, 102, 241, 0.28), transparent 58%)",
            pointerEvents: "none",
          },
        })}
      >
        <div className={css({ position: "relative", zIndex: 1 })}>
          <Logo to="/" onDark size={36} textSize="18px" className={css({ mb: "7" })} />
          <h1
            className={css({
              fontSize: { base: "3xl", md: "4xl" },
              fontWeight: "bold",
              letterSpacing: "tight",
              lineHeight: "1.1",
              maxW: "18ch",
              mb: "4",
            })}
          >
            La forma más simple de gestionar tus turnos.
          </h1>
          <p className={css({ maxW: "34ch", color: "rgba(255,255,255,0.78)", fontSize: "sm", lineHeight: "1.6" })}>
            Gestiona reservas, recordatorios y cobros en una experiencia clara y profesional.
          </p>
        </div>
        <div className={css({ position: "relative", zIndex: 1, display: "flex", gap: "2", flexWrap: "wrap" })}>
          <Tag text="Reservas 24/7" />
          <Tag text="Pagos con seña" />
          <Tag text="Avisos automáticos" />
        </div>
      </section>

      <section
        className={css({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { base: "6", md: "12" },
          bg: "linear-gradient(180deg, #f8fafc, #eef2f7)",
        })}
      >
        <FadeIn>
          <div className={css({ width: "100%", maxW: "560px", p: { base: "2", md: "4" } })}>
            <div className={css({ mb: "8" })}>
              <p className={css({ fontSize: "xs", letterSpacing: "widest", color: "#64748b", mb: "2" })}>ACCESO PROFESIONALES</p>
              <h2 className={css({ fontSize: { base: "3xl", md: "4xl" }, fontWeight: "bold", color: "#0f172a", lineHeight: "1.05" })}>
                Iniciar sesión
              </h2>
              <p className={css({ fontSize: "md", color: "#64748b", mt: "2" })}>Gestiona turnos, clientes y cobros desde un solo lugar.</p>
            </div>

            <div
              className={css({
                bg: "white",
                rounded: "xl",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                p: { base: "6", md: "7" },
                boxShadow: "0 24px 48px rgba(15, 23, 42, 0.10)",
              })}
            >
              {showResetOk && !error ? (
                <p className={css({ fontSize: "sm", color: "green.700", mb: "4" })}>
                  Tu contraseña fue actualizada. Iniciá sesión con la nueva.
                </p>
              ) : null}
              {showInvalid || error ? (
                <p className={css({ fontSize: "sm", color: "red.600", mb: "4" })}>
                  {error ?? "Email o contraseña incorrectos."}
                </p>
              ) : null}
              <form onSubmit={(e) => void onSubmit(e)} className={css({ display: "flex", flexDir: "column", gap: "4" })}>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Email</span>
                  <input
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="tu@email.com"
                    className={css({
                      h: "12",
                      px: "4",
                      fontSize: "md",
                      borderWidth: "1px",
                      borderColor: "#dbe2ea",
                      rounded: "lg",
                      bg: "#f8fafc",
                      _focus: {
                        outline: "none",
                        bg: "white",
                        borderColor: "#334155",
                        boxShadow: "0 0 0 3px rgba(51, 65, 85, 0.12)",
                      },
                    })}
                  />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Contraseña</span>
                  <PasswordInput
                    name="password"
                    required
                    autoComplete="current-password"
                    placeholder="********"
                    className={css({
                      h: "12",
                      px: "4",
                      fontSize: "md",
                      borderWidth: "1px",
                      borderColor: "#dbe2ea",
                      rounded: "lg",
                      bg: "#f8fafc",
                      _focus: {
                        outline: "none",
                        bg: "white",
                        borderColor: "#334155",
                        boxShadow: "0 0 0 3px rgba(51, 65, 85, 0.12)",
                      },
                    })}
                  />
                </label>
                <p className={css({ textAlign: "right", mt: "-2", mb: "1" })}>
                  <Link
                    to="/auth/forgot-password"
                    className={css({ fontSize: "sm", color: "#4f46e5", fontWeight: "medium", textDecoration: "underline" })}
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </p>
                <button
                  type="submit"
                  disabled={busy}
                  className={css({
                    h: "12",
                    rounded: "lg",
                    fontWeight: "semibold",
                    bg: "#0f172a",
                    color: "white",
                    border: "none",
                    cursor: busy ? "wait" : "pointer",
                    _hover: { opacity: 0.95 },
                  })}
                >
                  {busy ? "Iniciando…" : "Iniciar sesión"}
                </button>
              </form>
              <p className={css({ mt: "6", pt: "4", borderTopWidth: "1px", borderColor: "#e2e8f0", fontSize: "sm", color: "#64748b", textAlign: "center" })}>
                ¿No tenés cuenta?{" "}
                <Link to="/auth/register" className={css({ color: "#0f172a", fontWeight: "semibold", textDecoration: "underline" })}>
                  Registrate
                </Link>
              </p>
            </div>
          </div>
        </FadeIn>
      </section>
    </main>
  );
}

function Tag({ text }: { text: string }) {
  return (
    <span
      className={css({
        fontSize: "xs",
        px: "3",
        py: "1.5",
        rounded: "md",
        borderWidth: "1px",
        borderColor: "rgba(255,255,255,0.25)",
        color: "rgba(255,255,255,0.82)",
        bg: "rgba(255,255,255,0.04)",
      })}
    >
      {text}
    </span>
  );
}
